import { useFieldErrors } from '../hooks/useFieldErrors';
import { Flag } from '../components/shared/Flag';
import { useState, Fragment } from 'react';
import { useResetOnOpen } from '../hooks/useResetOnOpen';
import { NumberInput } from '../components/shared/NumberInput';
import { useRanchoStore } from '../store/rancho.store';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router';
import {
  Text, Button, Spinner, Input, Badge, Field, Dropdown, Option,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AddRegular, EditRegular, DeleteRegular, CloudAdd24Regular } from '@fluentui/react-icons';
import { fetchLotes } from '../api/lotes.api';
import {
  fetchRanchos, createRancho, updateRancho, deleteRancho,
  createWarehouse, deleteWarehouse, updateWarehouse,
  type Rancho,
} from '../api/rancho.api';
import { fetchWarehouses } from '../api/inventory.api';
import {
  fetchTelegramGroups, createTelegramGroup, updateTelegramGroup, deleteTelegramGroup,
  type TelegramGroup,
} from '../api/telegram-groups.api';
import { useT, LOCALE_OPTIONS } from '../i18n';
import { useLocaleStore } from '../store/locale.store';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePermission } from '../hooks/usePermission';
import type { Lote } from '../types/api.types';
import { MapPointPickerDialog } from '../components/shared/MapPointPickerDialog';
import { TelegramGroupPurposeDialog } from '../components/supervision/TelegramGroupPurposeDialog';
import { GroupWatchedPanel } from '../components/supervision/GroupWatchedPanel';
import { createGroupInvite } from '../api/supervision.api';
import { PageTitle } from '../components/shared/PageTitle';
import { SubTabs } from '../components/shared/SubTabs';

const ADMIN_TABS = ['ranchos', 'bodegas', 'grupos'] as const;
type AdminTab = typeof ADMIN_TABS[number];

/** Roles que se pueden fijar a un grupo de Telegram (SUPER_ADMIN no: no tiene sentido enfocar al rol total). */
const GROUP_ROLES = ['ADMIN', 'AGRONOMIST', 'SUPERVISOR', 'WAREHOUSE_MANAGER', 'FIELD_WORKER', 'AUDITOR', 'INVESTOR'] as const;

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '24px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  table:    { width: '100%', borderCollapse: 'collapse' as const, fontSize: '13px' },
  th: {
    textAlign: 'left' as const,
    padding: '8px 6px',
    color: tokens.colorNeutralForeground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    fontSize: '12px',
    fontWeight: '600',
  },
  td: {
    padding: '8px 6px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: '13px',
  },
});

// ─── Rancho Form Dialog ──────────────────────────────────────────────────────

function RanchoFormDialog({
  open, onClose, initial,
}: { open: boolean; onClose: () => void; initial?: Rancho | null }) {
  const t      = useT();
  const qc     = useQueryClient();
  const isEdit = !!initial;
  const [name,      setName]      = useState(initial?.name ?? '');
  const [ubicacion, setUbicacion] = useState(initial?.ubicacion ?? '');
  const [hectareas, setHectareas] = useState(initial?.hectareas?.toString() ?? '');
  const [latitude,  setLatitude]  = useState(initial?.latitude?.toString() ?? '');
  const [longitude, setLongitude] = useState(initial?.longitude?.toString() ?? '');
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [error,     setError]     = useState('');
  const fe = useFieldErrors();

  useResetOnOpen(open, () => {
    fe.clear();
    setName(initial?.name ?? '');
    setUbicacion(initial?.ubicacion ?? '');
    setHectareas(initial?.hectareas?.toString() ?? '');
    setLatitude(initial?.latitude?.toString() ?? '');
    setLongitude(initial?.longitude?.toString() ?? '');
    setMapPickerOpen(false);
    setError('');
  });

  const createMut = useMutation({
    mutationFn: () => createRancho({ name: name.trim(), ubicacion: ubicacion.trim() || undefined, hectareas: hectareas ? parseFloat(hectareas) : undefined, latitude: latitude ? parseFloat(latitude) : undefined, longitude: longitude ? parseFloat(longitude) : undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ranchos'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.ranchosAdmin.createError),
  });
  const updateMut = useMutation({
    mutationFn: () => updateRancho(initial!.ranchoId, { name: name.trim(), ubicacion: ubicacion.trim() || undefined, hectareas: hectareas ? parseFloat(hectareas) : undefined, latitude: latitude ? parseFloat(latitude) : undefined, longitude: longitude ? parseFloat(longitude) : undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ranchos'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.ranchosAdmin.updateError),
  });

  const saving = createMut.isPending || updateMut.isPending;

  return (
    <>
      <Dialog open={open && !mapPickerOpen} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
        <DialogSurface style={{ maxWidth: '420px' }}>
          <DialogBody>
            <DialogTitle>{isEdit ? `${t.ranchosAdmin.editRancho} — ${initial?.name}` : t.ranchosAdmin.newRancho}</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
                <Field label={t.ranchosAdmin.fieldName} {...fe.props('name')}>
                  <Input value={name} onChange={(_, d) => { setName(d.value); fe.clear('name'); }} placeholder={t.ranchosAdmin.fieldNamePlaceholder} disabled={saving} />
                </Field>
                <Field label={t.ranchosAdmin.fieldLocation}>
                  <Input value={ubicacion} onChange={(_, d) => setUbicacion(d.value)} placeholder={t.ranchosAdmin.fieldLocationPlaceholder} disabled={saving} />
                </Field>
                <Field label={t.ranchosAdmin.fieldHectareas}>
                  <NumberInput value={hectareas} onValueChange={(v) => setHectareas(v)} placeholder={t.ranchosAdmin.fieldHectareasPlaceholder} disabled={saving} />
                </Field>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Field label={t.ranchosAdmin.fieldLatitude} style={{ flex: 1 }}>
                    <Input type="number" value={latitude} onChange={(_, d) => setLatitude(d.value)} disabled={saving} />
                  </Field>
                  <Field label={t.ranchosAdmin.fieldLongitude} style={{ flex: 1 }}>
                    <Input type="number" value={longitude} onChange={(_, d) => setLongitude(d.value)} disabled={saving} />
                  </Field>
                </div>
                <Button appearance="secondary" size="small" onClick={() => setMapPickerOpen(true)} disabled={saving}>
                  {t.ranchosAdmin.useMapBtn}
                </Button>
                {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={onClose} disabled={saving}>{t.ranchosAdmin.cancel}</Button>
              <Button
                appearance="primary"
                onClick={() => { setError(''); if (!fe.validate({ name: !name.trim() && t.ranchosAdmin.nameRequired })) return; isEdit ? updateMut.mutate() : createMut.mutate(); }}
                disabled={saving}
                icon={saving ? <Spinner size="tiny" /> : undefined}
              >
                {isEdit ? t.ranchosAdmin.saveChanges : t.ranchosAdmin.createRancho}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {/* Hermano del Dialog del formulario, no hijo de su DialogSurface — anidado
          dentro rompía el focus-trap: al cerrar este picker, el formulario padre
          quedaba sin foco de teclado (el mouse seguía funcionando). */}
      <MapPointPickerDialog
        open={mapPickerOpen}
        onClose={() => setMapPickerOpen(false)}
        initialLat={latitude && !isNaN(parseFloat(latitude)) ? parseFloat(latitude) : undefined}
        initialLon={longitude && !isNaN(parseFloat(longitude)) ? parseFloat(longitude) : undefined}
        onConfirm={(lat, lon) => { setLatitude(lat.toString()); setLongitude(lon.toString()); }}
      />
    </>
  );
}

// ─── Warehouse Edit Dialog (nombre + alcance; no toca stock ni movimientos) ──

function WarehouseEditDialog({ warehouse, onClose, ranchos }: {
  warehouse: { warehouseId: string; name: string; ranchoId?: string | null } | null;
  onClose: () => void;
  ranchos: Rancho[];
}) {
  const t  = useT();
  const qc = useQueryClient();
  const [name,  setName]  = useState('');
  const [scope, setScope] = useState(SHARED_WAREHOUSE);
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  useResetOnOpen(!!warehouse, () => {
    setName(warehouse?.name ?? '');
    setScope(warehouse?.ranchoId ?? SHARED_WAREHOUSE);
    setError('');
    fe.clear();
  });

  const mut = useMutation({
    mutationFn: () => updateWarehouse(warehouse!.warehouseId, {
      name: name.trim(),
      ranchoId: scope === SHARED_WAREHOUSE ? null : scope,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['warehouses'] }); onClose(); },
    onError:   (e: any) => setError(e?.response?.data?.error ?? t.ranchosAdmin.warehouseUpdateError),
  });

  return (
    <Dialog open={!!warehouse} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '380px' }}>
        <DialogBody>
          <DialogTitle>{t.ranchosAdmin.editWarehouse}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <Field label={t.ranchosAdmin.fieldWarehouseName} {...fe.props('name')}>
                <Input value={name} onChange={(_, d) => { setName(d.value); fe.clear('name'); }} placeholder={t.ranchosAdmin.fieldWarehouseNamePlaceholder} disabled={mut.isPending} />
              </Field>
              <Field label={t.ranchosAdmin.fieldWarehouseRancho} hint={scope === SHARED_WAREHOUSE ? t.ranchosAdmin.warehouseSharedHint : undefined}>
                <Dropdown
                  data-testid="warehouse-edit-scope"
                  value={scope === SHARED_WAREHOUSE ? t.ranchosAdmin.warehouseSharedOption : (ranchos.find(r => r.ranchoId === scope)?.name ?? '')}
                  selectedOptions={[scope]}
                  disabled={mut.isPending}
                  onOptionSelect={(_, d) => setScope(d.optionValue ?? SHARED_WAREHOUSE)}
                >
                  <Option value={SHARED_WAREHOUSE} text={t.ranchosAdmin.warehouseSharedOption}>{t.ranchosAdmin.warehouseSharedOption}</Option>
                  {ranchos.map(r => <Option key={r.ranchoId} value={r.ranchoId} text={r.name}>{r.name}</Option>)}
                </Dropdown>
              </Field>
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={mut.isPending}>{t.ranchosAdmin.cancel}</Button>
            <Button
              appearance="primary"
              onClick={() => { setError(''); if (!fe.validate({ name: !name.trim() && t.ranchosAdmin.nameRequired })) return; mut.mutate(); }}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {t.ranchosAdmin.saveWarehouse}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── Warehouse Form Dialog ────────────────────────────────────────────────────

const SHARED_WAREHOUSE = '__shared__';

function WarehouseFormDialog({ open, onClose, ranchos }: { open: boolean; onClose: () => void; ranchos: Rancho[] }) {
  const t  = useT();
  const qc = useQueryClient();
  const [scope,     setScope]     = useState(SHARED_WAREHOUSE);
  const [name,      setName]      = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [error,     setError]     = useState('');
  const fe = useFieldErrors();

  useResetOnOpen(open, () => { setName(''); setUbicacion(''); setScope(SHARED_WAREHOUSE); setError(''); fe.clear(); });

  const mut = useMutation({
    mutationFn: () => createWarehouse({
      name: name.trim(), ubicacion: ubicacion.trim() || undefined,
      ...(scope !== SHARED_WAREHOUSE && { ranchoId: scope }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['warehouses'] }); onClose(); },
    onError:   (e: any) => setError(e?.response?.data?.error ?? t.ranchosAdmin.warehouseCreateError),
  });

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '380px' }}>
        <DialogBody>
          <DialogTitle>{t.ranchosAdmin.newWarehouse}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <Field label={t.ranchosAdmin.fieldWarehouseName} {...fe.props('name')}>
                <Input value={name} onChange={(_, d) => { setName(d.value); fe.clear('name'); }} placeholder={t.ranchosAdmin.fieldWarehouseNamePlaceholder} disabled={mut.isPending} />
              </Field>
              <Field label={t.ranchosAdmin.fieldWarehouseRancho} hint={scope === SHARED_WAREHOUSE ? t.ranchosAdmin.warehouseSharedHint : undefined}>
                <Dropdown
                  data-testid="warehouse-scope"
                  value={scope === SHARED_WAREHOUSE ? t.ranchosAdmin.warehouseSharedOption : (ranchos.find(r => r.ranchoId === scope)?.name ?? '')}
                  selectedOptions={[scope]}
                  disabled={mut.isPending}
                  onOptionSelect={(_, d) => setScope(d.optionValue ?? SHARED_WAREHOUSE)}
                >
                  <Option value={SHARED_WAREHOUSE} text={t.ranchosAdmin.warehouseSharedOption}>{t.ranchosAdmin.warehouseSharedOption}</Option>
                  {ranchos.map(r => <Option key={r.ranchoId} value={r.ranchoId} text={r.name}>{r.name}</Option>)}
                </Dropdown>
              </Field>
              <Field label={t.ranchosAdmin.fieldLocation}>
                <Input value={ubicacion} onChange={(_, d) => setUbicacion(d.value)} placeholder={t.ranchosAdmin.fieldWarehouseLocationPlaceholder} disabled={mut.isPending} />
              </Field>
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={mut.isPending}>{t.ranchosAdmin.cancel}</Button>
            <Button
              appearance="primary"
              onClick={() => { setError(''); if (!fe.validate({ name: !name.trim() && t.ranchosAdmin.nameRequired })) return; mut.mutate(); }}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {t.ranchosAdmin.createWarehouse}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── Telegram Group Form Dialog ────────────────────────────────────────────────

function TelegramGroupFormDialog({
  open, onClose, ranchos, lotes,
}: { open: boolean; onClose: () => void; ranchos: Rancho[]; lotes: Lote[] }) {
  const t  = useT();
  const qc = useQueryClient();
  const botUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string | undefined;
  const [chatId,   setChatId]   = useState('');
  const [ranchoId, setRanchoId] = useState('');
  const [loteId,   setLoteId]   = useState('');
  const [label,    setLabel]    = useState('');
  const locale = useLocaleStore(s => s.locale);
  const [language, setLanguage] = useState<'es' | 'en' | 'zh'>(locale);
  const [error,    setError]    = useState('');
  const fe = useFieldErrors();
  const activeRanchoId = useRanchoStore(s => s.activeRanchoId);

  // Siempre vacío al abrir (rancho activo preseleccionado si existe) para no
  // vincular un grupo al rancho equivocado con datos de una apertura anterior.
  useResetOnOpen(open, () => {
    setChatId(''); setLabel(''); setLoteId(''); setError(''); setLanguage(locale); fe.clear();
    setRanchoId(activeRanchoId && ranchos.some(r => r.ranchoId === activeRanchoId) ? activeRanchoId : '');
  });

  const lotesForRancho = lotes.filter(l => l.ranchoId === ranchoId);

  const mut = useMutation({
    mutationFn: () => createTelegramGroup({
      chatId: chatId.trim(),
      ranchoId,
      loteId: loteId || null,
      label: label.trim(),
      language,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['telegram-groups'] });
      setChatId(''); setLabel(''); setRanchoId(''); setLoteId(''); setError('');
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.telegramGroupsAdmin.createError),
  });

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '420px' }}>
        <DialogBody>
          <DialogTitle>{t.telegramGroupsAdmin.newGroup}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground2, whiteSpace: 'pre-line' }}>
                {t.telegramGroupsAdmin.addBotHint.replace('{bot}', botUsername ? `@${botUsername}` : 'Agrovid')}
              </Text>
              <Field label={t.telegramGroupsAdmin.fieldChatId} hint={t.telegramGroupsAdmin.fieldChatIdHint} {...fe.props('chatId')}>
                <Input value={chatId} onChange={(_, d) => { setChatId(d.value); fe.clear('chatId'); }} placeholder={t.telegramGroupsAdmin.fieldChatIdPlaceholder} disabled={mut.isPending} />
              </Field>
              <Field label={t.telegramGroupsAdmin.fieldLabel} {...fe.props('label')}>
                <Input value={label} onChange={(_, d) => { setLabel(d.value); fe.clear('label'); }} placeholder={t.telegramGroupsAdmin.fieldLabelPlaceholder} disabled={mut.isPending} />
              </Field>
              <Field label={t.telegramGroupsAdmin.fieldLanguage} hint={t.telegramGroupsAdmin.fieldLanguageHint}>
                <Dropdown
                  value={LOCALE_OPTIONS.find(o => o.value === language)?.label ?? ''}
                  selectedOptions={[language]}
                  disabled={mut.isPending}
                  onOptionSelect={(_, d) => setLanguage((d.optionValue as 'es' | 'en' | 'zh') ?? locale)}
                >
                  {LOCALE_OPTIONS.map(o => <Option key={o.value} value={o.value} text={o.label}>{<><Flag iso={o.flagIso} /> {o.label}</>}</Option>)}
                </Dropdown>
              </Field>
              <Field label={t.telegramGroupsAdmin.fieldRancho} {...fe.props('ranchoId')}>
                <Dropdown
                  value={ranchos.find(r => r.ranchoId === ranchoId)?.name ?? ''}
                  placeholder={t.telegramGroupsAdmin.fieldRanchoPlaceholder}
                  disabled={mut.isPending}
                  onOptionSelect={(_, d) => { setRanchoId(d.optionValue ?? ''); setLoteId(''); fe.clear('ranchoId'); }}
                >
                  {ranchos.map(r => <Option key={r.ranchoId} value={r.ranchoId}>{r.name}</Option>)}
                </Dropdown>
              </Field>
              <Field label={t.telegramGroupsAdmin.fieldLote}>
                <Dropdown
                  value={lotesForRancho.find(l => l.loteId === loteId)?.name ?? t.telegramGroupsAdmin.fieldLotePlaceholder}
                  placeholder={t.telegramGroupsAdmin.fieldLotePlaceholder}
                  disabled={mut.isPending || !ranchoId}
                  onOptionSelect={(_, d) => setLoteId(d.optionValue ?? '')}
                >
                  <Option value="">{t.telegramGroupsAdmin.fieldLotePlaceholder}</Option>
                  {lotesForRancho.map(l => <Option key={l.loteId} value={l.loteId}>{l.name}</Option>)}
                </Dropdown>
              </Field>
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => { setChatId(''); setLabel(''); setRanchoId(''); setLoteId(''); setError(''); onClose(); }} disabled={mut.isPending}>{t.telegramGroupsAdmin.cancel}</Button>
            <Button
              appearance="primary"
              onClick={() => {
                setError('');
                const ok = fe.validate({
                  chatId: !chatId.trim() && t.telegramGroupsAdmin.chatIdRequired,
                  label: !label.trim() && t.telegramGroupsAdmin.labelRequired,
                  ranchoId: !ranchoId && t.telegramGroupsAdmin.ranchoRequired,
                });
                if (!ok) return;
                mut.mutate();
              }}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {t.telegramGroupsAdmin.save}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export function RanchosAdminPage() {
  const s = useStyles();
  const t = useT();
  usePageTitle(t.pageTitle.ranchos);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();

  const isAdmin = usePermission('admin:ranchos');
  const canBulkImport = usePermission('admin:bulk-import');

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: AdminTab = (ADMIN_TABS as readonly string[]).includes(tabParam ?? '') ? (tabParam as AdminTab) : 'ranchos';
  const selectTab = (tab: AdminTab) =>
    setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', tab); return next; }, { replace: true });

  // Rancho CRUD state
  const [ranchoFormOpen,      setRanchoFormOpen]      = useState(false);
  const [editRancho,          setEditRancho]          = useState<Rancho | null>(null);
  const [deleteRanchoTarget,  setDeleteRanchoTarget]  = useState<Rancho | null>(null);
  const [deleteGroupTarget,   setDeleteGroupTarget]   = useState<TelegramGroup | null>(null);

  // Warehouse CRUD state
  const [warehouseFormOpen,       setWarehouseFormOpen]       = useState(false);
  const [deleteWarehouseTarget,   setDeleteWarehouseTarget]   = useState<{ warehouseId: string; name: string } | null>(null);
  const [editWarehouseTarget,     setEditWarehouseTarget]     = useState<{ warehouseId: string; name: string; ranchoId?: string | null } | null>(null);

  // Telegram groups state
  const [telegramGroupFormOpen, setTelegramGroupFormOpen] = useState(false);
  const [purposeTarget, setPurposeTarget] = useState<TelegramGroup | null>(null);
  const [expandedChatId, setExpandedChatId] = useState<string | null>(null);
  const [inviteNotice, setInviteNotice] = useState<{ ok: boolean; text: string } | null>(null);

  const { data: ranchos   = [], isLoading: loadingRanchos   } = useQuery<Rancho[]>({ queryKey: ['ranchos'],    queryFn: fetchRanchos });
  const { data: warehouses = [], isLoading: loadingWarehouses } = useQuery({ queryKey: ['warehouses'], queryFn: fetchWarehouses });
  const { data: lotes      = [] } = useQuery<Lote[]>({ queryKey: ['lotes'],      queryFn: () => fetchLotes() });
  const { data: telegramGroups = [], isLoading: loadingTelegramGroups } =
    useQuery<TelegramGroup[]>({ queryKey: ['telegram-groups'], queryFn: fetchTelegramGroups });

  const deleteRanchoMut = useMutation({
    mutationFn: (r: Rancho) => deleteRancho(r.ranchoId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ranchos'] }); setDeleteRanchoTarget(null); },
  });

  const deleteWarehouseMut = useMutation({
    mutationFn: (wh: { warehouseId: string }) => deleteWarehouse(wh.warehouseId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['warehouses'] }); setDeleteWarehouseTarget(null); },
  });

  const setLanguageMut = useMutation({
    mutationFn: ({ chatId, language }: { chatId: string; language: 'es' | 'en' | 'zh' }) =>
      updateTelegramGroup(chatId, { language }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['telegram-groups'] }),
  });
  const setGroupRoleMut = useMutation({
    // '' = sin rol de enfoque (el bot usa los roles de quien escribe).
    mutationFn: ({ chatId, role }: { chatId: string; role: string }) => updateTelegramGroup(chatId, { role: role || null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['telegram-groups'] }),
  });
  const deleteGroupMut = useMutation({
    mutationFn: (g: TelegramGroup) => deleteTelegramGroup(g.chatId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['telegram-groups'] }); setDeleteGroupTarget(null); },
  });
  const inviteMut = useMutation({
    mutationFn: (chatId: string) => createGroupInvite(chatId),
    onSuccess: () => setInviteNotice({ ok: true, text: t.telegramGroupsAdmin.inviteSent }),
    onError: (e: any) => setInviteNotice({ ok: false, text: e?.response?.data?.error ?? t.telegramGroupsAdmin.inviteError }),
  });
  const toggleTelegramGroupMut = useMutation({
    mutationFn: (g: TelegramGroup) => updateTelegramGroup(g.chatId, { active: !g.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['telegram-groups'] }),
  });

  if (!isAdmin) {
    return (
      <div className={s.root}>
        <PageTitle>{t.ranchosAdmin.pageTitle}</PageTitle>
        <Text>{t.ranchosAdmin.noPermission}</Text>
      </div>
    );
  }

  return (
    <div className={s.root}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <PageTitle>{t.ranchosAdmin.pageTitle}</PageTitle>
        {canBulkImport && (
          <Button
            appearance="outline"
            size="small"
            icon={<CloudAdd24Regular />}
            onClick={() => navigate(`/${tenantId}/bulk-import`)}
          >
            {t.nav.bulkImport}
          </Button>
        )}
      </div>

      <SubTabs selected={activeTab} onSelect={(v) => selectTab(v as AdminTab)} tabs={[{ value: 'ranchos', label: t.ranchosAdmin.tabRanchos }, { value: 'bodegas', label: t.ranchosAdmin.tabWarehouses }, { value: 'grupos', label: t.ranchosAdmin.tabTelegramGroups }]} />

      {activeTab === 'ranchos' && (<>
      {/* ── Ranchos ── */}
      <div className={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text className={s.sectionTitle}>{t.ranchosAdmin.ranchosTitle}</Text>
          <Button size="small" appearance="primary" icon={<AddRegular />}
            onClick={() => { setEditRancho(null); setRanchoFormOpen(true); }}>
            {t.ranchosAdmin.newRancho}
          </Button>
        </div>
        {loadingRanchos ? <Spinner size="small" /> : (
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>{t.ranchosAdmin.colName}</th>
                <th className={s.th}>{t.ranchosAdmin.colLocation}</th>
                <th className={s.th} style={{ textAlign: 'right' }}>{t.ranchosAdmin.colHectareas}</th>
                <th className={s.th}>{t.common.status}</th>
                <th className={s.th} style={{ textAlign: 'center' }}>{t.ranchosAdmin.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {ranchos.length === 0 ? (
                <tr><td colSpan={5} className={s.td} style={{ textAlign: 'center', color: tokens.colorNeutralForeground3 }}>{t.ranchosAdmin.noRanchos}</td></tr>
              ) : ranchos.map(r => (
                <tr key={r.ranchoId}>
                  <td className={s.td} style={{ fontWeight: '600' }}>{r.name}</td>
                  <td className={s.td} style={{ color: tokens.colorNeutralForeground3 }}>{r.ubicacion ?? '—'}</td>
                  <td className={s.td} style={{ textAlign: 'right' }}>{r.hectareas != null ? `${r.hectareas} ha` : '—'}</td>
                  <td className={s.td}>
                    <Badge appearance="tint" color={r.status === 'ACTIVE' ? 'success' : 'warning'} size="small">
                      {r.status && r.status !== 'ACTIVE' ? r.status : t.ranchosAdmin.statusActive}
                    </Badge>
                  </td>
                  <td className={s.td} style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                      <Button size="small" appearance="subtle" icon={<EditRegular />}
                        onClick={() => { setEditRancho(r); setRanchoFormOpen(true); }} />
                      <Button size="small" appearance="subtle" icon={<DeleteRegular />}
                        style={{ color: tokens.colorStatusDangerForeground1 }}
                        onClick={() => setDeleteRanchoTarget(r)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
        <Link to={`/${tenantId}/campo`}>{t.ranchosAdmin.lotesManagedInField}</Link>
      </Text>
      </>)}

      {activeTab === 'bodegas' && (
      <>
      {/* ── Bodegas ── */}
      <div className={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text className={s.sectionTitle}>{t.ranchosAdmin.warehousesTitle}</Text>
          <Button size="small" appearance="primary" icon={<AddRegular />} onClick={() => setWarehouseFormOpen(true)}>
            {t.ranchosAdmin.newWarehouse}
          </Button>
        </div>
        {loadingWarehouses ? <Spinner size="small" /> : (
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>{t.ranchosAdmin.colId}</th>
                <th className={s.th}>{t.ranchosAdmin.colName}</th>
                <th className={s.th}>{t.ranchosAdmin.colLocation}</th>
                <th className={s.th}>{t.ranchosAdmin.colWarehouseScope}</th>
                <th className={s.th} style={{ textAlign: 'center' }}>{t.ranchosAdmin.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {warehouses.length === 0 ? (
                <tr><td colSpan={5} className={s.td} style={{ textAlign: 'center', color: tokens.colorNeutralForeground3 }}>{t.ranchosAdmin.noWarehouses}</td></tr>
              ) : warehouses.map((wh: any) => (
                <tr key={wh.warehouseId}>
                  <td className={s.td} style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>{wh.warehouseId}</td>
                  <td className={s.td} style={{ fontWeight: '600' }}>{wh.name}</td>
                  <td className={s.td} style={{ color: tokens.colorNeutralForeground3 }}>{wh.ubicacion ?? '—'}</td>
                  <td className={s.td}>
                    {wh.ranchoId
                      ? (ranchos.find(r => r.ranchoId === wh.ranchoId)?.name ?? '—')
                      : <Badge appearance="tint" color="informative">{t.ranchosAdmin.warehouseSharedOption}</Badge>}
                  </td>
                  <td className={s.td} style={{ textAlign: 'center' }}>
                    <Button size="small" appearance="subtle" icon={<EditRegular />}
                      aria-label={t.ranchosAdmin.editWarehouse}
                      onClick={() => setEditWarehouseTarget(wh)} />
                    <Button size="small" appearance="subtle" icon={<DeleteRegular />}
                      style={{ color: tokens.colorStatusDangerForeground1 }}
                      onClick={() => setDeleteWarehouseTarget(wh)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      </>)}

      {activeTab === 'grupos' && (
      <>
      {/* ── Grupos de Telegram ── */}
      <div className={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text className={s.sectionTitle}>{t.telegramGroupsAdmin.title}</Text>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>{t.telegramGroupsAdmin.subtitle}</Text>
          </div>
          <Button size="small" appearance="primary" icon={<AddRegular />} onClick={() => setTelegramGroupFormOpen(true)}>
            {t.telegramGroupsAdmin.newGroup}
          </Button>
        </div>
        {inviteNotice && (
          <Text role="status" size={200} style={{ color: inviteNotice.ok ? tokens.colorStatusSuccessForeground1 : tokens.colorStatusDangerForeground1 }}>
            {inviteNotice.text}
          </Text>
        )}
        {loadingTelegramGroups ? <Spinner size="small" /> : (
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>{t.telegramGroupsAdmin.colLabel}</th>
                <th className={s.th}>{t.telegramGroupsAdmin.colChatId}</th>
                <th className={s.th}>{t.telegramGroupsAdmin.colRancho}</th>
                <th className={s.th}>{t.telegramGroupsAdmin.colLote}</th>
                <th className={s.th}>{t.telegramGroupsAdmin.colPurpose}</th>
                <th className={s.th}>{t.telegramGroupsAdmin.colMode}</th>
                <th className={s.th}>{t.telegramGroupsAdmin.colRole}</th>
                <th className={s.th}>{t.telegramGroupsAdmin.colLanguage}</th>
                <th className={s.th}>{t.telegramGroupsAdmin.colLinkedBy}</th>
                <th className={s.th}>{t.telegramGroupsAdmin.colStatus}</th>
                <th className={s.th} style={{ textAlign: 'center' }}>{t.telegramGroupsAdmin.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {telegramGroups.length === 0 ? (
                <tr><td colSpan={11} className={s.td} style={{ textAlign: 'center', color: tokens.colorNeutralForeground3 }}>{t.telegramGroupsAdmin.noGroups}</td></tr>
              ) : telegramGroups.map(g => (
                <Fragment key={g.chatId}>
                <tr>
                  <td className={s.td} style={{ fontWeight: '600' }}>{g.label}</td>
                  <td className={s.td} style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>{g.chatId}</td>
                  <td className={s.td}>{ranchos.find(r => r.ranchoId === g.ranchoId)?.name
                    ?? <span style={{ color: tokens.colorStatusDangerForeground1 }}>{t.telegramGroupsAdmin.ranchoDeleted}</span>}</td>
                  <td className={s.td} style={{ color: tokens.colorNeutralForeground3 }}>
                    {g.loteId ? (lotes.find(l => l.loteId === g.loteId)?.name ?? g.loteId) : t.telegramGroupsAdmin.wholeRancho}
                  </td>
                  <td className={s.td}>
                    <Badge appearance="tint" color={g.proposito === 'SUPERVISION' ? 'informative' : 'brand'} size="small">
                      {g.proposito === 'SUPERVISION' ? t.telegramGroupsAdmin.purposeSupervision : t.telegramGroupsAdmin.purposeCapture}
                    </Badge>
                  </td>
                  <td className={s.td} style={{ color: tokens.colorNeutralForeground3 }}>
                    {g.proposito === 'SUPERVISION' ? '—'
                      : g.modoCaptura === 'SOLO_MENCION' ? t.telegramGroupsAdmin.modeMention : t.telegramGroupsAdmin.modeAll}
                  </td>
                  <td className={s.td}>
                    <Dropdown
                      size="small"
                      aria-label={t.telegramGroupsAdmin.colRole}
                      value={g.role ? (t.settings.roles[g.role as keyof typeof t.settings.roles] ?? g.role) : t.telegramGroupsAdmin.roleAny}
                      selectedOptions={[g.role ?? '']}
                      disabled={setGroupRoleMut.isPending}
                      onOptionSelect={(_, d) => setGroupRoleMut.mutate({ chatId: g.chatId, role: d.optionValue ?? '' })}
                      style={{ minWidth: '130px' }}
                    >
                      <Option value="" text={t.telegramGroupsAdmin.roleAny}>{t.telegramGroupsAdmin.roleAny}</Option>
                      {GROUP_ROLES.map(r => <Option key={r} value={r} text={t.settings.roles[r] ?? r}>{t.settings.roles[r] ?? r}</Option>)}
                    </Dropdown>
                  </td>
                  <td className={s.td}>
                    <Dropdown
                      size="small"
                      aria-label={t.telegramGroupsAdmin.colLanguage}
                      value={LOCALE_OPTIONS.find(o => o.value === g.language)?.label ?? t.telegramGroupsAdmin.languageDefault}
                      selectedOptions={g.language ? [g.language] : []}
                      disabled={setLanguageMut.isPending}
                      onOptionSelect={(_, d) => d.optionValue && setLanguageMut.mutate({ chatId: g.chatId, language: d.optionValue as 'es' | 'en' | 'zh' })}
                      style={{ minWidth: '110px' }}
                    >
                      {LOCALE_OPTIONS.map(o => <Option key={o.value} value={o.value} text={o.label}>{<><Flag iso={o.flagIso} /> {o.label}</>}</Option>)}
                    </Dropdown>
                  </td>
                  <td className={s.td} style={{ color: tokens.colorNeutralForeground3 }}>{g.linkedBy}</td>
                  <td className={s.td}>
                    <Badge appearance="tint" color={g.active ? 'success' : 'warning'} size="small">
                      {g.active ? t.telegramGroupsAdmin.statusActive : t.telegramGroupsAdmin.statusInactive}
                    </Badge>
                  </td>
                  <td className={s.td} style={{ textAlign: 'center' }}>
                    <Button size="small" appearance="subtle" onClick={() => setPurposeTarget(g)}>
                      {t.telegramGroupsAdmin.configure}
                    </Button>
                    {g.proposito === 'SUPERVISION' && (<>
                      <Button
                        size="small"
                        appearance="subtle"
                        disabled={inviteMut.isPending}
                        onClick={() => inviteMut.mutate(g.chatId)}
                      >
                        {t.telegramGroupsAdmin.inviteSupervisor}
                      </Button>
                      <Button
                        size="small"
                        appearance="subtle"
                        aria-expanded={expandedChatId === g.chatId}
                        onClick={() => setExpandedChatId(expandedChatId === g.chatId ? null : g.chatId)}
                      >
                        {t.telegramGroupsAdmin.watchedToggle}
                      </Button>
                    </>)}
                    <Button
                      size="small"
                      appearance="subtle"
                      disabled={toggleTelegramGroupMut.isPending}
                      onClick={() => toggleTelegramGroupMut.mutate(g)}
                    >
                      {g.active ? t.telegramGroupsAdmin.deactivate : t.telegramGroupsAdmin.activate}
                    </Button>
                    <Button
                      size="small"
                      appearance="subtle"
                      style={{ color: tokens.colorStatusDangerForeground1 }}
                      disabled={deleteGroupMut.isPending}
                      onClick={() => setDeleteGroupTarget(g)}
                    >
                      {t.telegramGroupsAdmin.delete}
                    </Button>
                  </td>
                </tr>
                {g.proposito === 'SUPERVISION' && expandedChatId === g.chatId && (
                  <tr>
                    <td colSpan={11} className={s.td}><GroupWatchedPanel group={g} /></td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      </>)}

      {/* ── Dialogs ── */}
      <TelegramGroupPurposeDialog
        key={purposeTarget?.chatId ?? 'no-group'}
        group={purposeTarget}
        onClose={() => setPurposeTarget(null)}
      />
      <RanchoFormDialog
        key={editRancho?.ranchoId ?? 'new-rancho'}
        open={ranchoFormOpen}
        onClose={() => setRanchoFormOpen(false)}
        initial={editRancho}
      />

      <Dialog open={!!deleteRanchoTarget} onOpenChange={(_, d) => !d.open && setDeleteRanchoTarget(null)} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t.ranchosAdmin.deleteRanchoTitle}</DialogTitle>
            <DialogContent>
              <Text>{t.ranchosAdmin.deleteRanchoConfirmPrefix}<strong>{deleteRanchoTarget?.name}</strong>{t.ranchosAdmin.deleteRanchoConfirmSuffix}</Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteRanchoTarget(null)} disabled={deleteRanchoMut.isPending}>{t.ranchosAdmin.cancel}</Button>
              <Button appearance="primary"
                style={{ backgroundColor: tokens.colorStatusDangerBackground3 }}
                onClick={() => deleteRanchoTarget && deleteRanchoMut.mutate(deleteRanchoTarget)}
                disabled={deleteRanchoMut.isPending}
                icon={deleteRanchoMut.isPending ? <Spinner size="tiny" /> : undefined}
              >{t.ranchosAdmin.delete}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={!!deleteGroupTarget} onOpenChange={(_, d) => !d.open && setDeleteGroupTarget(null)} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t.telegramGroupsAdmin.deleteTitle}</DialogTitle>
            <DialogContent>
              <Text>{t.telegramGroupsAdmin.deleteBody.replace('{name}', deleteGroupTarget?.label ?? '')}</Text>
              {deleteGroupMut.isError && (
                <Text style={{ color: tokens.colorStatusDangerForeground1, display: 'block', marginTop: '8px' }}>{t.telegramGroupsAdmin.deleteError}</Text>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteGroupTarget(null)} disabled={deleteGroupMut.isPending}>{t.telegramGroupsAdmin.cancel}</Button>
              <Button appearance="primary"
                style={{ backgroundColor: tokens.colorStatusDangerBackground3 }}
                onClick={() => deleteGroupTarget && deleteGroupMut.mutate(deleteGroupTarget)}
                disabled={deleteGroupMut.isPending}
                icon={deleteGroupMut.isPending ? <Spinner size="tiny" /> : undefined}
              >{t.telegramGroupsAdmin.delete}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <TelegramGroupFormDialog
        open={telegramGroupFormOpen}
        onClose={() => setTelegramGroupFormOpen(false)}
        ranchos={ranchos}
        lotes={lotes}
      />

      <WarehouseFormDialog open={warehouseFormOpen} onClose={() => setWarehouseFormOpen(false)} ranchos={ranchos} />
      <WarehouseEditDialog warehouse={editWarehouseTarget} onClose={() => setEditWarehouseTarget(null)} ranchos={ranchos} />

      <Dialog open={!!deleteWarehouseTarget} onOpenChange={(_, d) => !d.open && setDeleteWarehouseTarget(null)} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t.ranchosAdmin.deleteWarehouseTitle}</DialogTitle>
            <DialogContent>
              <Text>{t.ranchosAdmin.deleteWarehouseConfirmPrefix}<strong>{deleteWarehouseTarget?.name}</strong>{t.ranchosAdmin.deleteWarehouseConfirmSuffix}</Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteWarehouseTarget(null)} disabled={deleteWarehouseMut.isPending}>{t.ranchosAdmin.cancel}</Button>
              <Button appearance="primary"
                style={{ backgroundColor: tokens.colorStatusDangerBackground3 }}
                onClick={() => deleteWarehouseTarget && deleteWarehouseMut.mutate(deleteWarehouseTarget)}
                disabled={deleteWarehouseMut.isPending}
                icon={deleteWarehouseMut.isPending ? <Spinner size="tiny" /> : undefined}
              >{t.ranchosAdmin.delete}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
